import { Component, inject, Injector, input, runInInjectionContext } from '@angular/core';

import { Store } from '@ngrx/store';
import { lastValueFrom, take } from 'rxjs';

import { filterDefined } from '@core/common-legacy';
import { TellentCurrentUserSelectors } from '@core/tellent-data';
import {
  CurrentUserProfileDialogComponent,
  EditMemberAdminDialogComponent,
} from '@core/tellent-smart-ui';
import {
  APP_GUID,
  InputSignalLike,
  TellentOrgAbilities,
  TellentWebComponent,
} from '@core/tellent-types';
import { useProtectedControlsCallback } from '@core/user-interface-routing-logic';

import { getCurrentUserProfileConfig } from '../current-user-profile-dialog/current-user-profile-dialog-wrap.component';
import { DialogWrapComponent } from '../dialog/dialog-wrap.component';

const getEditMemberDialogConfig = () => {
  const protectedControls = useProtectedControlsCallback();

  return {
    containerOptions: { fullscreenOnMobile: true },
    closeGuard: () => protectedControls(() => true),
  };
};

@Component({
  selector: 'x-edit-member-dialog',
  template: ``,
  imports: [],
})
export class EditMemberDialogWrapComponent extends DialogWrapComponent<
  EditMemberAdminDialogComponent | CurrentUserProfileDialogComponent
> {
  #isSelf: boolean;
  #canManageUsers: boolean;
  #injector = inject(Injector);

  public readonly appGuid = inject(APP_GUID);

  public get config() {
    return runInInjectionContext(this.#injector, () => {
      switch (this.component) {
        case EditMemberAdminDialogComponent:
          return getEditMemberDialogConfig();

        case CurrentUserProfileDialogComponent:
          return getCurrentUserProfileConfig();

        default:
          return {};
      }
    });
  }

  public get component(): any {
    if (this.#canManageUsers) {
      return EditMemberAdminDialogComponent;
    } else {
      if (!this.#isSelf) {
        console.warn(
          `Tried to access member-edit without required permission, fallback to current user editing`,
        );
      }

      return CurrentUserProfileDialogComponent;
    }
  }

  public memberId: InputSignalLike<string> = input.required<string>();

  public currentUser = lastValueFrom(
    inject(Store).select(TellentCurrentUserSelectors.data).pipe(filterDefined, take(1)),
  );

  public async resolveDialogData() {
    const user = await this.currentUser;
    const member = user?.members.find(m => m.seats.some(s => s.app.guid === this.appGuid))!;

    this.#canManageUsers = member.abilities.includes(TellentOrgAbilities.ManageUsers);
    this.#isSelf = this.memberId() === user.guid;

    return Promise.resolve({
      memberId: this.memberId(),
      context: 'client',
    });
  }
}

export type TellentEditMemberDialogWebComponent = TellentWebComponent<
  EditMemberDialogWrapComponent,
  'open' | 'memberId' | 'afterClosed'
>;
